/**
 * Memory System API
 *
 * API client for interacting with Agentserve Memory endpoints.
 * These endpoints provide memory statistics, operations history,
 * context retrieval, and debugging capabilities.
 */

import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const memoryApiFetch = createServiceFetch(config.api.agentserveUrl)

const { api: apiConfig, auth: authConfig } = config

// =============================================================================
// Types
// =============================================================================

export interface MemoryStats {
  working_count: number
  episodic_count: number
  semantic_count: number
  procedural_count: number
  graph_node_count: number
  graph_edge_count: number
  total_memories: number
}

/** Frontend debug-panel status (normalized). Backend may also send completed/failed. */
export type MemoryOperationStatus =
  | 'success'
  | 'failure'
  | 'pending'
  | 'completed'
  | 'failed'

export interface MemoryOperation {
  id: string
  timestamp: string
  operation_type: 'store' | 'recall' | 'extract' | 'consolidate' | 'delete'
  operation_subtype?: string
  status: MemoryOperationStatus
  duration_ms: number
  input_tokens?: number
  output_tokens?: number
  cost_cents?: number
  memory_type?: string
  query?: string
  result_count?: number
  error?: string
  metadata?: Record<string, unknown>
}

/** Map Agentserve usage-tracker statuses onto MemoryDebugPanel icons/totals. */
export function normalizeMemoryOperationStatus(
  status: MemoryOperationStatus | string | undefined
): 'success' | 'failure' | 'pending' {
  switch (status) {
    case 'success':
    case 'completed':
      return 'success'
    case 'failure':
    case 'failed':
      return 'failure'
    default:
      return 'pending'
  }
}

export interface OperationsResponse {
  operations: MemoryOperation[]
  total: number
  has_more: boolean
}

export interface RetrievalScore {
  memory_id: string
  content_preview: string
  memory_type: string
  final_score: number
  vector_score?: number
  keyword_score?: number
  graph_score?: number
  temporal_score?: number
  strategies_used?: string[]
}

export interface RetrievalScoresResponse {
  query: string
  scores: RetrievalScore[]
  strategies_used: string[]
  total_time_ms: number
}

export interface MemoryContextResponse {
  context: string
  estimated_tokens: number
  max_tokens: number
  include_cross_session: boolean
  query: string
  session_id: string | null
}

export interface MemoryRecord {
  id: string
  tenant_id: string
  agent_id: string
  user_id: string
  content: string
  memory_type: 'working' | 'episodic' | 'semantic' | 'procedural'
  epistemic_state?: 'observed' | 'inferred' | 'believed' | 'refuted' | 'deprecated'
  importance: number
  confidence: number
  created_at: string
  updated_at: string
  metadata?: Record<string, unknown>
}

export interface MemoryRecordList {
  memories: MemoryRecord[]
  total: number
  query?: string
  memory_types?: string[]
}

export interface BulkDeleteMemoriesResponse {
  deleted: number
  failed: number
  errors: Array<{ memory_id: string; error: string }>
}

// Few-Shot Example Types
export type FewShotExampleType = 'positive' | 'negative' | 'edge_case' | 'correction'

export interface FewShotExample {
  id: string
  input_text: string
  output_text: string
  reasoning?: string
  example_type: FewShotExampleType
  task_category?: string
  complexity_level?: number
  tags?: string[]
  quality_score?: number
  use_count?: number
  success_count?: number
  failure_count?: number
  is_active?: boolean
  is_verified?: boolean
  created_at?: string
  updated_at?: string
}

export interface FewShotExampleCreate {
  input_text: string
  output_text: string
  reasoning?: string
  example_type: FewShotExampleType
  task_category?: string
  complexity_level?: number
  tags?: string[]
}

export interface FewShotExampleUpdate extends Partial<FewShotExampleCreate> {
  is_active?: boolean
  is_verified?: boolean
}

export interface FewShotQuality {
  quality_score: number
  relevance: number
  clarity: number
  consistency: number
  suggestions?: string[]
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Make authenticated request to Agentserve
 */
async function agentserveRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(authConfig.tokenKey)
  const tenantId = localStorage.getItem('tagp-tenant-id')

  const url = `${apiConfig.agentserveUrl}${endpoint}`

  const response = await memoryApiFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData?.detail || errorData?.error || `Request failed: ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

// =============================================================================
// Memory API
// =============================================================================

// Base path for memory endpoints (Agentserve serves at /api/v1/memory)
const MEMORY_BASE = '/api/v1/memory'

export const memoryApi = {
  /**
   * Get memory statistics for an agent
   */
  getStats: (agentId: string, userId?: string) => {
    const params = new URLSearchParams()
    if (userId) params.append('user_id', userId)
    const query = params.toString() ? `?${params.toString()}` : ''
    return agentserveRequest<MemoryStats>(`${MEMORY_BASE}/agents/${agentId}/stats${query}`)
  },

  /**
   * Get recent memory operations for debugging
   */
  getOperations: (
    agentId: string,
    options?: {
      userId?: string
      operationType?: string
      limit?: number
    }
  ) => {
    const params = new URLSearchParams()
    if (options?.userId) params.append('user_id', options.userId)
    if (options?.operationType) params.append('operation_type', options.operationType)
    if (options?.limit) params.append('limit', options.limit.toString())
    const query = params.toString() ? `?${params.toString()}` : ''
    return agentserveRequest<OperationsResponse>(
      `${MEMORY_BASE}/agents/${agentId}/operations${query}`
    )
  },

  /**
   * Get memory context formatted for prompt injection
   */
  getContext: (
    agentId: string,
    userId: string,
    options?: {
      query?: string
      sessionId?: string
      maxTokens?: number
      includeCrossSession?: boolean
    }
  ) => {
    const params = new URLSearchParams()
    if (options?.query) params.append('query', options.query)
    if (options?.sessionId) params.append('session_id', options.sessionId)
    if (options?.maxTokens) params.append('max_tokens', options.maxTokens.toString())
    if (options?.includeCrossSession !== undefined) {
      params.append('include_cross_session', options.includeCrossSession.toString())
    }
    const query = params.toString() ? `?${params.toString()}` : ''
    return agentserveRequest<MemoryContextResponse>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/context${query}`
    )
  },

  /**
   * Get retrieval scores for debugging
   */
  getRetrievalScores: (agentId: string, userId: string, searchQuery: string) => {
    return agentserveRequest<RetrievalScoresResponse>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/retrieval-scores`,
      {
        method: 'POST',
        body: JSON.stringify({ query: searchQuery }),
      }
    )
  },

  /**
   * Recall memories for a user
   */
  recallMemories: (
    agentId: string,
    userId: string,
    options?: {
      query?: string
      memoryTypes?: string[]
      topK?: number
      useHybrid?: boolean
      /** When true, API returns only deprecated/refuted memories */
      includeDeprecated?: boolean
      /** Deployment environment (DEV, TEST, QA, UAT, PROD) for row isolation */
      environment?: string
    }
  ) => {
    return agentserveRequest<MemoryRecordList>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/recall`,
      {
        method: 'POST',
        body: JSON.stringify({
          query: options?.query || '',
          memory_types: options?.memoryTypes,
          top_k: options?.topK || 50,
          use_hybrid: options?.useHybrid ?? true,
          include_deprecated: options?.includeDeprecated ?? false,
          ...(options?.environment != null && options.environment !== ''
            ? { environment: options.environment }
            : {}),
        }),
      }
    )
  },

  /**
   * Search memories with filters
   */
  searchMemories: (
    agentId: string,
    userId: string,
    options?: {
      query?: string
      memoryTypes?: string[]
      topK?: number
      minImportance?: number
      minConfidence?: number
      environment?: string
    }
  ) => {
    return agentserveRequest<MemoryRecordList>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/search`,
      {
        method: 'POST',
        body: JSON.stringify({
          query: options?.query || '',
          memory_types: options?.memoryTypes,
          top_k: options?.topK || 50,
          min_importance: options?.minImportance,
          min_confidence: options?.minConfidence,
          ...(options?.environment != null && options.environment !== ''
            ? { environment: options.environment }
            : {}),
        }),
      }
    )
  },

  /**
   * Delete a specific memory
   */
  deleteMemory: (agentId: string, userId: string, memoryId: string) => {
    return agentserveRequest<void>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/memories/${memoryId}`,
      { method: 'DELETE' }
    )
  },

  /**
   * Delete multiple memories by ID (partial success allowed).
   */
  bulkDeleteMemories: (
    agentId: string,
    userId: string,
    memoryIds: string[]
  ) => {
    return agentserveRequest<BulkDeleteMemoriesResponse>(
      `${MEMORY_BASE}/agents/${agentId}/users/${userId}/memories/bulk-delete`,
      {
        method: 'POST',
        body: JSON.stringify({ memory_ids: memoryIds }),
      }
    )
  },

  /**
   * Get memory system health status
   */
  getHealth: () => {
    return agentserveRequest<{
      status: string
      timestamp: string
      components: Record<string, { status: string; message: string }>
      providers: string[]
    }>(`${MEMORY_BASE}/health`)
  },

  /**
   * Get memory system status
   */
  getStatus: () => {
    return agentserveRequest<{
      available: boolean
      providers: string[]
      message: string
    }>(`${MEMORY_BASE}/status`)
  },

  // ==========================================================================
  // Few-Shot Examples (Optimize My Agent)
  // ==========================================================================

  /**
   * List few-shot examples for an agent
   */
  listFewShotExamples: (
    agentId: string,
    options?: {
      exampleType?: string
      limit?: number
      offset?: number
    }
  ) => {
    const params = new URLSearchParams()
    if (options?.exampleType) params.append('example_type', options.exampleType)
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    const query = params.toString() ? `?${params.toString()}` : ''
    return agentserveRequest<FewShotExample[]>(`${MEMORY_BASE}/agents/${agentId}/few-shot${query}`)
  },

  /**
   * Create a new few-shot example
   */
  createFewShotExample: (agentId: string, example: FewShotExampleCreate) => {
    return agentserveRequest<FewShotExample>(`${MEMORY_BASE}/agents/${agentId}/few-shot`, {
      method: 'POST',
      body: JSON.stringify(example),
    })
  },

  /**
   * Update a few-shot example
   */
  updateFewShotExample: (
    agentId: string,
    exampleId: string,
    updates: FewShotExampleUpdate
  ) => {
    return agentserveRequest<FewShotExample>(
      `${MEMORY_BASE}/agents/${agentId}/few-shot/${exampleId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      }
    )
  },

  /**
   * Delete a few-shot example
   */
  deleteFewShotExample: (agentId: string, exampleId: string) => {
    return agentserveRequest<void>(`${MEMORY_BASE}/agents/${agentId}/few-shot/${exampleId}`, {
      method: 'DELETE',
    })
  },

  /**
   * Get quality score for a few-shot example
   */
  getFewShotQuality: (agentId: string, exampleId: string) => {
    return agentserveRequest<FewShotQuality>(
      `${MEMORY_BASE}/agents/${agentId}/few-shot/${exampleId}/quality`
    )
  },

  /**
   * Bulk import few-shot examples
   */
  importFewShotExamples: (agentId: string, examples: FewShotExampleCreate[]) => {
    return agentserveRequest<{
      imported: number
      skipped: number
      errors: Array<{ index: number; error: string }>
    }>(`${MEMORY_BASE}/agents/${agentId}/few-shot/import`, {
      method: 'POST',
      body: JSON.stringify({ examples }),
    })
  },
}
